/* eslint-disable no-console */
import { SpinnerLoader } from '@assets/icons'
import { Button, ButtonAction, CustomSelect, ErrorForm, HeaderPage, InputFile, InputForm, SwitchDefault } from '@components/index'
import { useYupValidation } from '@hooks/index'
import { REAL_ESTATE_TYPE } from '@interfaces/realEstates/enums/realEstateType'
import { ICreateUpdateRealEstate } from '@interfaces/realEstates/iCreateRealEstate'
import RealEstateModal, { data } from '@pages/realEstates/realEstateModal'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { errorHandler, GetPostCode } from '@services/index'
import RealEstateService from '@services/requests/realEstateService'
import { maskCoin, states } from '@utils/index'
import maskCep from '@utils/mask/cepMask'
import latlngMask from '@utils/mask/latlngMask'
import { realEstatesSchema } from '@yupSchemas/index'
import React from 'react'
import { Controller, FieldArrayWithId, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { BsBookmarkFill } from 'react-icons/bs'
import { MdEdit } from 'react-icons/md'
import { RiDeleteBin5Fill } from 'react-icons/ri'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function CreateRealEstate(): React.ReactElement {
    const { isLoaded: isMapsLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    })

    const navigate = useNavigate()
    const [isLoading, setIsLoading] = React.useState(false)
    const [dataModal, setdataModal] = React.useState<data | undefined>(undefined)

    const categories = [
        { title: 'Em Breve', value: REAL_ESTATE_TYPE.COMMING_SOON },
        { title: 'Empreendimento', value: REAL_ESTATE_TYPE.VENTURE },
        { title: 'Lançamento', value: REAL_ESTATE_TYPE.RELEASE },
        { title: 'Selecionado', value: REAL_ESTATE_TYPE.SELECTED },
    ]

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm<ICreateUpdateRealEstate>({
        resolver: useYupValidation(realEstatesSchema),
        defaultValues: {
            type: REAL_ESTATE_TYPE.COMMING_SOON,
        }
    })

    const methods = useForm<ICreateUpdateRealEstate>()

    const postcode = watch('postcode')
    const watchFieldType = watch('type')
    const watchFielCoverName = watch('coverName')
    const watchLatitude = parseFloat(watch('latitude'))
    const watchLongitude = parseFloat(watch('longitude'))
    // const watchGallery = watch('gallery')

    const latitude = watchLatitude ? watchLatitude : -27.125866002275146
    const longitude = watchLongitude ? watchLongitude : -48.60332015901804

    const {
        fields: additionalInfo,
        remove: removeAdditionalInfo,
        append: appendAdditionalInfo,
        update: updateAdditionalInfo
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'additionalInfo' })

    const {
        fields: banner,
        remove: removeBanner,
        append: appendBanner
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'banner' })

    const {
        fields: gallery,
        remove: removeGallery,
        append: appendGallery
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'gallery' })

    React.useEffect(() => {
        getDataCep(postcode)
    }, [postcode])

    async function submit(values: ICreateUpdateRealEstate): Promise<void> {
        const formData = new FormData()
        formData.append('type', values.type)
        formData.append('active', JSON.stringify(values.active))
        formData.append('name', values.name)
        formData.append('listPosition', JSON.stringify(values.listPosition))

        if (watchFieldType === REAL_ESTATE_TYPE.SELECTED) {
            formData.append('price', JSON.stringify(parseFloat(values.price)))
            formData.append('priceObservation', values.priceObservation)
            formData.append('cnaBanner', JSON.stringify(values.cnaBanner))
        }
        if (watchFieldType === REAL_ESTATE_TYPE.RELEASE || watchFieldType === REAL_ESTATE_TYPE.VENTURE) {
            if (values.ventureLogo)
                formData.append('ventureLogo', values.ventureLogo as File)
            if (values.catalog)
                formData.append('catalog', values.catalog as File)
            if (values.urlVideo)
                formData.append('urlVideo', values.urlVideo)
            if (values.url360)
                formData.append('url360', values.url360)
            if (values.additionalInfo && values.additionalInfo.length)
                formData.append('additionalInfo', JSON.stringify(values.additionalInfo))
            if (values.banner && values.banner.length > 0) {
                for (const bannerFile of values.banner) {
                    formData.append('banner', bannerFile.file as File)
                }
            }
        }
        if (watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON) {
            formData.append('description', values.description)
            formData.append('postcode', values.postcode)
            formData.append('state', values.state)
            formData.append('address', values.address)
            formData.append('city', values.city)
            formData.append('number', values.number)
            formData.append('latitude', values.latitude)
            formData.append('longitude', values.longitude)
            formData.append('coverName', values.coverName)
            for (const galleryFile of values.gallery) {
                formData.append('gallery', galleryFile.file as File)
            }
        }

        setIsLoading(true)
        try {
            await RealEstateService.create(formData)
            toast('Imóvel salvo com sucesso!', { type: 'success' })
            navigate('/imoveis')
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function readerFile(file: File): string {
        return URL.createObjectURL(file)
    }

    function appendImage(append: () => void, fileBin: File, fields: FieldArrayWithId<ICreateUpdateRealEstate, 'gallery' | 'banner', 'id'>[]): void {
        const repeated = fields.find(field => field.file?.name === fileBin.name)
        if (repeated) toast('Há imagens duplicadas na mesma categoria', { type: 'error' })
        else append()
    }

    function listDescriptionAddInfo(description: string): string[] {
        return description.split('\n')
    }

    async function getDataCep(value: string): Promise<void> {
        try {
            if (!value || value.replace(/\D/g, '').length !== 8) {
                return
            }
            const dataCep = await GetPostCode(value)
            if (dataCep && dataCep.data) {
                setValue('state', dataCep.data.state)
                setValue('address', dataCep.data.streetAddress)
                setValue('city', dataCep.data.city)
                setValue('latitude', dataCep.data.latitude.toString())
                setValue('longitude', dataCep.data.longitude.toString())
            } else {
                toast('CEP não encontrado!', { type: 'warning' })
            }
        } catch (error) {
            toast('CEP não encontrado!', { type: 'warning' })
        }
    }

    return (
        <div className="card">
            <div className="create-update-real-estate">
                <HeaderPage title="Novo Imóvel" />
                <FormProvider {...methods} >
                    <form onSubmit={handleSubmit(submit)} className='form'>
                        <section className='section-1'>
                            <div className='default-fields'>
                                <Controller
                                    name='type'
                                    control={control}
                                    render={({ field: { value } }) => {
                                        return (
                                            <CustomSelect
                                                label='Tipo'
                                                defaultValue={value}
                                                name='type'
                                                value={value}
                                                onChange={(e) => reset({
                                                    type: e.target.value as REAL_ESTATE_TYPE
                                                })}
                                                options={categories}
                                                style={{ marginBottom: '1.7rem' }}
                                            />
                                        )
                                    }}
                                />
                                <div className="box-controller name">
                                    <Controller
                                        name="name"
                                        defaultValue={''}
                                        control={control}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <InputForm
                                                    label="Nome"
                                                    placeholder='Nome do imóvel'
                                                    name="name"
                                                    value={value}
                                                    onChange={onChange}
                                                    size='small'
                                                />
                                            )
                                        }}
                                    />
                                    <ErrorForm error={errors.name?.message} />
                                </div>
                                <div className="box-controller">
                                    <Controller
                                        name="listPosition"
                                        defaultValue={0}
                                        control={control}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <InputForm
                                                    type="number"
                                                    placeholder="Posição"
                                                    label="Posição"
                                                    name="listPosition"
                                                    value={value}
                                                    onChange={onChange}
                                                    style={{ width: '9rem' }}
                                                    size='small'
                                                />
                                            )
                                        }}
                                    />
                                    <ErrorForm error={errors.listPosition?.message} />
                                </div>
                                <div className='box-switchs'>
                                    <Controller
                                        name='active'
                                        defaultValue={true}
                                        control={control}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <div className='status'>
                                                    <label htmlFor='switchStatus' className='switchLabel'>Status</label>
                                                    <div>
                                                        <SwitchDefault
                                                            value={value}
                                                            id='switchStatus'
                                                            defaultChecked
                                                            onChange={onChange}
                                                            title={'Ativar/Desativar'}
                                                            color='secondary'
                                                        />
                                                        <p className='small-2'>{value ? 'Ativo' : 'Inativo'}</p>
                                                    </div>
                                                </div>
                                            )
                                        }}
                                    />
                                    {watchFieldType === REAL_ESTATE_TYPE.SELECTED
                                        ?
                                        <Controller
                                            name='cnaBanner'
                                            defaultValue={true}
                                            control={control}
                                            render={({ field: { value, onChange } }) => {
                                                return (
                                                    <div className='status'>
                                                        <label htmlFor='switchBannerCna' className='switchLabel'>Exibir como Banner CNA</label>
                                                        <div>
                                                            <SwitchDefault
                                                                value={value}
                                                                id='switchBannerCna'
                                                                defaultChecked
                                                                onChange={onChange}
                                                                title={'Sim/Não'}
                                                                color='secondary'
                                                            />
                                                            <p className='small-2'>{value ? 'Sim' : 'Não'}</p>
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                        />
                                        :
                                        <></>
                                    }
                                </div>
                            </div>
                            {watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ?
                                <div className='logo-catalog-box'>
                                    <div className='logo-box'>
                                        <h6 className='input-title'>Logo do Empreendimento</h6>
                                        <InputFile name='ventureLogo' onChange={(value) => setValue('ventureLogo', value)} acceptFiles='image/jpeg, image/png' />
                                    </div>
                                    <div className='pdf-box'>
                                        <h6 className='input-title'>Catálogo PDF</h6>
                                        <InputFile name='catalog' onChange={(value) => setValue('catalog', value)} acceptFiles='application/pdf' />
                                    </div>
                                </div>
                                :
                                <></>
                            }
                            <div className={`too-many-fields 
                                ${watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'only-venture-release' : ''}`
                            }>
                                {watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON ?
                                    <div className="box-controller">
                                        <Controller
                                            name="description"
                                            defaultValue={''}
                                            control={control}
                                            render={({ field: { value, onChange } }) => {
                                                return (
                                                    <InputForm
                                                        label={`${watchFieldType === REAL_ESTATE_TYPE.VENTURE ? 'Descrição do Empreendimento'
                                                            : watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'Descrição do Lançamento'
                                                                : 'Descrição'}`}
                                                        placeholder={`${watchFieldType === REAL_ESTATE_TYPE.VENTURE ? 'Descrição do Empreendimento'
                                                            : watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'Descrição do Lançamento'
                                                                : 'Descrição'}`}
                                                        name="description"
                                                        value={value}
                                                        onChange={onChange}
                                                        multiline
                                                        rows={6}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                height: '10.362rem',
                                                                alignItems: 'flex-start',
                                                                boxSizing: 'border-box'
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                        />
                                        <ErrorForm error={errors.description?.message} />
                                    </div>
                                    : <></>
                                }
                                {watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ?
                                    <div className='additional-info'>
                                        <div className='header-additional-info'>
                                            <h5>Descrição dos apartamentos</h5>
                                            <Button
                                                type='button'
                                                onClick={() => {
                                                    setdataModal({
                                                        index: undefined,
                                                        description: '',
                                                        title: ''
                                                    })
                                                }}
                                                placeholder='Adicionar'
                                            />
                                        </div>
                                        <div className='box-additional-info'>
                                            {additionalInfo.map((item, index) => {
                                                if (item && item.title && item.description) {
                                                    return (
                                                        <div key={item.id} className='content-additional-info'>
                                                            <div className='header-box-additional-info'>
                                                                <h5>{item.title}</h5>
                                                                <button title='Editar' type='button' className='button-action-add-info' onClick={() => {
                                                                    setdataModal({
                                                                        index,
                                                                        title: additionalInfo[index].title,
                                                                        description: additionalInfo[index].description
                                                                    })
                                                                }}>
                                                                    <MdEdit className='icon-button-action' />
                                                                </button>
                                                                <button title='Deletar' type='button' className='button-action-add-info' onClick={() => removeAdditionalInfo(index)}>
                                                                    <RiDeleteBin5Fill className='icon-button-action' />
                                                                </button>
                                                            </div>
                                                            <ul className='list-add-info'>
                                                                {listDescriptionAddInfo(item.description).map((item, index) => (
                                                                    item ?
                                                                        <li key={index}>{item}</li>
                                                                        : undefined
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )
                                                }
                                            })}
                                        </div>
                                        <div className='box-urls'>
                                            <Controller
                                                name="urlVideo"
                                                control={control}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Url do Vídeo"
                                                            name="urlVideo"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                            <Controller
                                                name="url360"
                                                control={control}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Url 360°"
                                                            name="url360"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <></>
                                }
                                {watchFieldType === REAL_ESTATE_TYPE.SELECTED ?
                                    <div className='bottom-box'>
                                        <div className='left-box'>
                                            <div className="box-controller">
                                                <Controller
                                                    name="price"
                                                    control={control}
                                                    defaultValue={'0,00'}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Valor"
                                                                name="price"
                                                                value={value}
                                                                onChange={(e) => {
                                                                    onChange(maskCoin(e.target.value))
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 14
                                                                }}
                                                                InputProps={{
                                                                    startAdornment: 'R$',
                                                                }}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.price?.message} />
                                            </div>
                                        </div>
                                        <div className='right-box'>
                                            <Controller
                                                name="priceObservation"
                                                control={control}
                                                defaultValue={''}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Observações do valor"
                                                            placeholder="Observações do valor"
                                                            name="priceObservation"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <></>
                                }
                            </div>
                        </section>
                        {watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON ?
                            <>
                                <section className='section-2'>
                                    <h5>Localização</h5>
                                    <div>
                                        <div className='left-box'>
                                            <div className="box-controller name">
                                                <Controller
                                                    name="postcode"
                                                    control={control}
                                                    defaultValue=''
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="CEP"
                                                                placeholder='CEP do imóvel'
                                                                name="postcode"
                                                                value={maskCep(value)}
                                                                onChange={onChange}
                                                                size='small'
                                                                inputProps={{
                                                                    maxLength: 9
                                                                }}
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.postcode?.message} />
                                            </div>
                                            <div className="box-controller" style={{ gridColumn: 'span 2' }}>
                                                <Controller
                                                    name="city"
                                                    control={control}
                                                    defaultValue=''
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Cidade"
                                                                placeholder='Cidade do imóvel'
                                                                name="city"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.city?.message} />
                                            </div>
                                            <div className="box-controller" style={{ gridColumn: 'span 2' }}>
                                                <Controller
                                                    name="address"
                                                    defaultValue=''
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Endereço"
                                                                placeholder='Endereço do imóvel'
                                                                name="address"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.address?.message} />
                                            </div>
                                            <div className="box-controller">
                                                <Controller
                                                    name="number"
                                                    defaultValue={''}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Número"
                                                                placeholder='Número do imóvel'
                                                                name="number"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.number?.message} />
                                            </div>
                                            <div className="box-controller" style={{ gridColumn: 'span 3' }}>
                                                <Controller
                                                    name='state'
                                                    control={control}
                                                    defaultValue={'none'}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <CustomSelect
                                                                label='Estado'
                                                                placeholder='Selecione...'
                                                                name='state'
                                                                value={value}
                                                                onChange={onChange}
                                                                options={states}
                                                                isFirstNoneDefault
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.state?.message} />
                                            </div>
                                            <div className='lat-lng-box'>
                                                <div className="box-controller">
                                                    <Controller
                                                        name="latitude"
                                                        control={control}
                                                        defaultValue={latitude.toString()}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <InputForm
                                                                    label="Latitude"
                                                                    placeholder='Latitude'
                                                                    name="latitude"
                                                                    value={latlngMask(value)}
                                                                    onChange={onChange}
                                                                    size='small'
                                                                />
                                                            )
                                                        }}
                                                    />
                                                    <ErrorForm error={errors.latitude?.message} />
                                                </div>
                                                <div className="box-controller">
                                                    <Controller
                                                        name="longitude"
                                                        control={control}
                                                        defaultValue={longitude.toString()}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <InputForm
                                                                    label="Longitude"
                                                                    placeholder='Longitude'
                                                                    name="longitude"
                                                                    value={latlngMask(value)}
                                                                    onChange={onChange}
                                                                    size='small'
                                                                />
                                                            )
                                                        }}
                                                    />
                                                    <ErrorForm error={errors.longitude?.message} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='right-box'>
                                            {!isMapsLoaded ?
                                                <></>
                                                :
                                                <GoogleMap
                                                    zoom={15}
                                                    center={{
                                                        lat: latitude,
                                                        lng: longitude
                                                    }}
                                                    mapContainerClassName='map-container'
                                                    onClick={ev => {
                                                        if (ev.latLng) {
                                                            setValue('latitude', ev.latLng.lat().toString())
                                                            setValue('longitude', ev.latLng.lng().toString())
                                                        }
                                                    }}
                                                >
                                                    <Marker
                                                        position={{
                                                            lat: latitude,
                                                            lng: longitude
                                                        }}
                                                    />
                                                </GoogleMap>
                                            }
                                        </div>
                                    </div>
                                </section>
                                {watchFieldType === REAL_ESTATE_TYPE.RELEASE || watchFieldType === REAL_ESTATE_TYPE.VENTURE ?
                                    <section className='section-3'>
                                        <h5>Banners</h5>
                                        <div className='box-images'>
                                            {banner.map((item, index) => {
                                                return (
                                                    <div key={item.id} className='box-image'>
                                                        <img src={readerFile(item.file as File)} alt="banner" />
                                                        <div className='cover-image'>
                                                            <ButtonAction title='Deletar' type='button' onClick={() => removeBanner(index)}>
                                                                <RiDeleteBin5Fill className='icon-button-action' />
                                                            </ButtonAction>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <InputFile
                                                onChange={
                                                    fileBin => {
                                                        if (fileBin)
                                                            appendImage(() => appendBanner({ file: fileBin }), fileBin, banner)
                                                    }
                                                }
                                                multiple
                                                acceptFiles='image/jpeg, image/png'
                                            />
                                        </div>
                                    </section>
                                    : <></>
                                }
                                <section className='section-4'>
                                    <h5>Galeria</h5>
                                    <div className='box-images'>
                                        <Controller
                                            name="coverName"
                                            control={control}
                                            defaultValue=''
                                            render={({ field: { value } }) => {
                                                return (
                                                    <input
                                                        name={'coverName'}
                                                        value={value}
                                                        disabled
                                                        hidden
                                                    />
                                                )
                                            }}
                                        />
                                        {gallery.map((item, index) => {

                                            return (
                                                <div key={item.id} className='box-image'>
                                                    <img src={readerFile(item.file as File)} alt="imagem da galeria" />

                                                    {watchFielCoverName === item.file?.name ?
                                                        <div className='cover-checked'>
                                                            <BsBookmarkFill />
                                                        </div>
                                                        :
                                                        <></>
                                                    }

                                                    <div className='cover-image'>
                                                        <ButtonAction
                                                            title='Deletar'
                                                            type='button'
                                                            onClick={() => {
                                                                removeGallery(index)
                                                                setValue('coverName', '')
                                                            }}
                                                        >
                                                            <RiDeleteBin5Fill className='icon-button-action' />
                                                        </ButtonAction>
                                                        <ButtonAction
                                                            title='Definir como capa'
                                                            isDisabled={watchFielCoverName === item.file?.name}
                                                            type='button'
                                                            onClick={
                                                                () => setValue('coverName', item.file ? item.file.name : '')
                                                            }
                                                        >
                                                            <BsBookmarkFill className='icon-button-action' />
                                                        </ButtonAction>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <InputFile
                                            onChange={
                                                fileBin => {
                                                    if (fileBin)
                                                        appendImage(() => appendGallery({ file: fileBin }), fileBin, gallery)
                                                }
                                            }
                                            multiple
                                            acceptFiles='image/jpeg, image/png'
                                        />
                                    </div>
                                    <ErrorForm error={errors.gallery?.message} />
                                </section>
                            </>
                            :
                            <></>
                        }
                        <div className={`box-buttons ${watchFieldType === REAL_ESTATE_TYPE.COMMING_SOON ? 'box-buttons-small' : ''}`}>
                            <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate('/imoveis')} />
                            <Button type="submit" placeholder={'Salvar'} />
                        </div>
                        {isLoading ? (
                            <div className="loader-page">
                                <SpinnerLoader />
                            </div>
                        ) : (
                            <></>
                        )}
                    </form>
                </FormProvider>
            </div>
            <RealEstateModal
                data={dataModal}
                modelTitle='Descrição do Apartamento'
                labelTitle='Título do Apartamento'
                labelDescription='Descrição do Apartamento'
                appendChild={(e: data) => appendAdditionalInfo(e)}
                updateChild={(index, e) => updateAdditionalInfo(index, e)}
                closeModal={() => {
                    setdataModal(undefined)
                }}
            />
        </div>
    )
}
